import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { lovable } from "@/integrations/lovable";
import { clearCheckoutIntent, readCheckoutIntent, startCheckout, startParlayCheckout } from "@/lib/billing";

const RESET_SUCCESS_MESSAGE = "If an account exists, we'll send password reset instructions.";

function getResetRedirectUrl() {
  const isLocal = ["localhost", "127.0.0.1"].includes(window.location.hostname);
  return isLocal ? `${window.location.origin}/reset-password` : "https://aleaedge.com/reset-password";
}

export default function Auth() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  const isSignIn = useMemo(() => mode === "signin", [mode]);

  const resumeCheckoutIfNeeded = useCallback(async () => {
    const intent = readCheckoutIntent();
    if (!intent) return false;
    clearCheckoutIntent();
    try {
      if (intent.type === "pro") {
        await startCheckout();
        return true;
      }
      await startParlayCheckout(intent.parlayId);
      return true;
    } catch (error: any) {
      toast.error(error?.message ?? "Unable to resume checkout");
      return false;
    }
  }, []);

  const fallbackReturnTo = useCallback(() => {
    const returnTo = searchParams.get("returnTo");
    if (!returnTo || !returnTo.startsWith("/")) return "/dashboard";
    return returnTo;
  }, [searchParams]);

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data }) => {
      if (!data.session) return;
      const resumed = await resumeCheckoutIfNeeded();
      if (!resumed) nav(fallbackReturnTo());
    });
  }, [fallbackReturnTo, nav, resumeCheckoutIfNeeded]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "signup") {
        const { data, error } = await supabase.auth.signUp({
          email, password,
          options: { emailRedirectTo: window.location.origin + "/auth" + window.location.search },
        });
        if (error) throw error;
        if (data.session) {
          toast.success("Welcome to AleaEdge!");
          const resumed = await resumeCheckoutIfNeeded();
          if (!resumed) nav(fallbackReturnTo());
        } else {
          toast.success("Account created — check your email to confirm.");
        }
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        const resumed = await resumeCheckoutIfNeeded();
        if (!resumed) nav(fallbackReturnTo());
      }
    } catch (err: any) {
      const msg: string = err?.message ?? "";
      const code: string = err?.code ?? "";
      if (code === "over_email_send_rate_limit" || /rate limit|after \d+ seconds/i.test(msg)) {
        const secs = msg.match(/after (\d+) seconds/i)?.[1];
        toast.success(
          mode === "signup"
            ? `Account created — check your email to confirm.${secs ? ` You can request another link in ${secs}s.` : ""}`
            : `Please wait${secs ? ` ${secs}s` : " a moment"} before trying again.`
        );
      } else {
        toast.error(msg || "Something went wrong");
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogle() {
    const result = await lovable.auth.signInWithOAuth("google", { redirect_uri: window.location.origin + "/auth" + window.location.search });
    if (result.error) toast.error("Google sign-in failed");
  }

  async function handleForgotPassword() {
    if (!email) {
      toast.error("Enter your email to reset your password.");
      return;
    }

    setResetLoading(true);
    try {
      await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: getResetRedirectUrl(),
      });
      toast.success(RESET_SUCCESS_MESSAGE);
    } catch {
      toast.success(RESET_SUCCESS_MESSAGE);
    } finally {
      setResetLoading(false);
    }
  }

  return (
    <div className="container max-w-md py-20">
      <div className="te-card p-8">
        <h1 className="font-display text-3xl font-bold text-center">{mode === "signin" ? "Welcome back" : "Create your account"}</h1>
        <p className="text-muted-foreground text-center text-sm mt-1">Access the Top 5 AI Plays of the Day.</p>

        <form onSubmit={handleSubmit} className="space-y-4 mt-6">
          <div><Label>Email</Label><Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} /></div>
          <div><Label>Password</Label><Input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} /></div>
          {isSignIn && (
            <button
              type="button"
              onClick={handleForgotPassword}
              disabled={resetLoading}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              {resetLoading ? "Sending reset email…" : "Forgot password?"}
            </button>
          )}
          <Button type="submit" disabled={loading} className="w-full bg-gradient-primary text-primary-foreground hover:opacity-90">
            {loading ? "Please wait…" : mode === "signin" ? "Sign in" : "Sign up"}
          </Button>
        </form>

        <button onClick={() => setMode(mode === "signin" ? "signup" : "signin")} className="block mx-auto mt-5 text-sm text-muted-foreground hover:text-foreground">
          {mode === "signin" ? "Need an account? Sign up" : "Have an account? Sign in"}
        </button>
        <p className="text-xs text-center text-muted-foreground mt-6">
          By continuing you agree to our informational-only terms. <Link to="/" className="underline">Learn more</Link>.
        </p>
      </div>
    </div>
  );
}
